import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import React, { ReactNode, useCallback } from "react";
import { FlatList, ListRenderItemInfo, View } from "react-native";
import { api } from "../utils/convex-api";
import {
  BookingItem,
  SessionItem,
} from "@teachloop/backend/api/app/tutor/schedule/queries";
import TutorSessionCard from "../shared/tutor-session-card";
import TutorBookingCard from "../shared/tutor-booking-card";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useRouter } from "expo-router";
import { useSheet } from "../shared/sheet-provider";
import { SheetIds } from "../utils/sheet-ids";
import { TutorLessonSheetPayload, TutorLessonSheetResult } from "../sheets/tutor-lesson-sheet";
import ScheduleEmptyState from "./schedule-empty-state";

function ScheduleList({
  scheduleList,
  header,
  statusFilter,
}: {
  scheduleList:  SessionItem[] | BookingItem[];
  header: ReactNode;
  statusFilter?: "upcoming" | "requests" | "completed";
}) {
  const { open } = useSheet<TutorLessonSheetPayload, TutorLessonSheetResult>(SheetIds.TUTOR_LESSON);
  const responsiveWidth = useResponsiveWidth();
  const router = useRouter();
  const onReview = useCallback(
    (bookingId: Id<"bookings">) => {
      router.push(`/booking/review-request?bookingId=${bookingId}`);
    },
    [router]
  );
  const { mutateAsync: declineBooking } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.reviewLesson.mutations.declineBooking
    ),
  });

  const openCancelLessonScreen = useCallback((payload: {id: Id<'sessions'>}) => {
    router.push(`/(app)/cancel-lesson?lessonId=${payload.id}`);
  }, [router]);


  const onDecline = useCallback(
    (bookingId: Id<"bookings">, reason?: string) => {
      if (reason) {
        declineBooking({
          bookingId,
          declinedReason: reason,
        });
      }
    },
    [declineBooking]
  );

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<SessionItem | BookingItem>) => {
      const renderCard = () => {
        switch (item.type) {
          case "session":
            return (
              <TutorSessionCard
                key={item.id}
                open={open}
                openCancelLessonScreen={openCancelLessonScreen}
                session={{
                  sessionId: item.id,
                  bookingId: item.bookingId,
                  rescheduleCount: item.rescheduleCount,
                  subjectId: item.subjectId,
                  subjectName: item.subject,
                  type: item.sessionType,
                  deliveryMethod: item.deliveryMethod,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  bufferStartTime: item.bufferStartTime,
                  bufferEndTime: item.bufferEndTime,
                  student: item.student,
                  status: item.status,
                  yearGroupName: item.yearGroupName,
                  url: item.url,
                  tutorNotes: item.tutorNotes,
                  parentNotes: item.parentNotes,
                }}
              />
            );
          case "booking":
            return (
              <TutorBookingCard
                key={item.id}
                booking={{
                  bookingId: item.id,
                  student: item.student,
                  parentName: item.parentName,
                  subjectName: item.subject,
                  deliveryMethod: item.deliveryMethod,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  bufferStartTime: item.bufferStartTime,
                  bufferEndTime: item.bufferEndTime,
                  notes: item.notes,
                  sessionDate: item.startTime,
                  type: item.sessionType,
                }}
                onReview={() => onReview(item.id)}
                onDecline={(reason) => onDecline(item.id, reason)}
              />
            );

          default:
            return null;
        }
      };
      return <View className="mx-5">{renderCard()}</View>;
    },
    [onDecline, onReview, open, openCancelLessonScreen]
  );

  const getEmptyStateMessage = () => {
    switch (statusFilter) {
      case "upcoming":
        return "You have no upcoming lessons scheduled.";
      case "requests":
        return "You have no pending booking requests.";
      case "completed":
        return "You have no completed lessons yet.";
      default:
        return "You have no lessons scheduled.";
    }
  };

  const ListEmptyComponent = useCallback(() => {
    return <ScheduleEmptyState message={getEmptyStateMessage()} />;
  }, [statusFilter]);

  return (
    <FlatList
      data={scheduleList}
      renderItem={renderItem}
      ListHeaderComponent={() => header}
      ListEmptyComponent={ListEmptyComponent}
      ItemSeparatorComponent={() => <View className="h-4" />}
      ListFooterComponent={() => <View className="h-4" />}
      style={{ width: responsiveWidth, alignSelf: "center" }}
    />
  );
}

export default ScheduleList;
